import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  HeartHandshake,
  Mountain,
  Ticket,
  Headphones,
  Sparkles,
  Star,
  CheckCircle2,
} from "lucide-react";
import SEO from "@/components/SEO";
import TripCard from "@/components/TripCard";
import HeroVideos from "@/components/HeroVideos";
import Testimonials from "@/components/Testimonials";
import ContactForm from "@/components/ContactForm";
import { TRIPS } from "@/data/trips";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a65ad287-8b4d-434c-b754-d0d96cdf1004/images/015829978236da38c46b1bbd2a63ce7053b4dafdba02e81eaee1e18325289599.png";
const VALUE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a65ad287-8b4d-434c-b754-d0d96cdf1004/images/d7d53a748ed3ed7aafd0c42353654046b6cbcbc7dea82388adb0e6210eeee6a8.png";
const DIRECTOR_IMG = "https://clubamistad.es/wp-content/uploads/2025/11/Jose-Maria-Gonzalo-768x1024.jpg";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  name: "Club Amistad",
  url: "https://informacional.preview.emergentagent.com",
  description:
    "Agencia familiar de viajes de esquí desde 1969. Servicio integral, hoteles a pie de pista, monitores propios y clases para toda la familia.",
  telephone: "+34 914 020 331",
  email: "informacion@clubamistad.com",
  founder: { "@type": "Person", name: "José María Gonzalo Dueñas" },
  foundingDate: "1969",
  areaServed: "ES",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.7",
    reviewCount: "100",
    bestRating: "5",
    worstRating: "1",
  },
};

export default function HomePage() {
  return (
    <div className="bg-brand-bg">
      <SEO
        title="Viajes de esquí organizados en familia · Club Amistad Madrid (desde 1969)"
        description="Especialistas en viajes de esquí organizados en familia. Hoteles a pie de pista, monitores propios y clases para niños desde 4 años. Cerler, Masella y Sierra Nevada. 4,7/5 en Google."
        path="/"
        image={HERO_IMG}
        jsonLd={homeJsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroVideos />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />
        </div>

        <div className="container-prose relative z-10 py-28 md:py-40 text-white">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-cormorant italic text-sm md:text-base tracking-[0.24em] uppercase text-white/85 mb-5"
          >
            Viajes de esquí organizados en familia · Desde 1969
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-outfit text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-4xl text-white"
          >
            El viaje a la nieve que <em className="font-cormorant italic text-brand-accent">siempre</em> soñasteis,
            <br className="hidden md:block" />
            sin el estrés de planificarlo todo.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="mt-6 max-w-2xl font-manrope text-base md:text-xl text-white/85 leading-relaxed"
          >
            Llegáis, abrís la maleta y empieza la magia. Hoteles a pie de pista, forfaits en mano y
            monitores que se convierten en sus amigos. Vosotros solo tenéis una tarea: disfrutar y verles
            sonreír.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a href="#viajes" data-testid="hero-cta-trips" className="btn-accent">
              Ver nuestros viajes <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#dolor"
              data-testid="hero-cta-why"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-manrope text-sm md:text-base hover:bg-white/10 transition-all"
            >
              Por qué Club Amistad
            </a>
          </motion.div>

          <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-white/80 text-sm">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-warm" /> 56 años especializados en familias</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-warm" /> Monitores propios desde 4 años</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-warm" /> Guía 24/7 en destino</div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section id="dolor" className="section-y bg-brand-surface/60">
        <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="overline">Lo que de verdad pasa</p>
            <h2 className="h1-display mt-4">
              Planificarlo todo se parece demasiado a <em className="font-cormorant italic text-brand-accent">esto</em>, ¿a que sí?
            </h2>
            <p className="body-lg mt-6">
              La ilusión de un viaje familiar a la nieve es enorme. El camino para llegar allí… es otra
              historia. Conocemos cada pestaña abierta, cada Excel comparativo, cada noche dándole vueltas.
            </p>
            <blockquote className="mt-8 border-l-2 border-brand-accent pl-5 italic font-cormorant text-2xl md:text-3xl text-brand-primary leading-snug">
              “Siempre termino estresada… ojalá alguien resolviera todo por mí.”
            </blockquote>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
            {PAINS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="card-soft p-6 md:p-8"
                data-testid={`pain-${i}`}
              >
                <p className="font-cormorant italic text-brand-accent text-sm uppercase tracking-[0.2em]">0{i + 1}</p>
                <h3 className="font-outfit text-xl md:text-2xl text-brand-primary font-semibold mt-3">{p.title}</h3>
                <p className="font-manrope text-sm md:text-base text-brand-ink/75 leading-relaxed mt-3">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section id="nosotros" className="section-y">
        <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden">
              <img
                src={VALUE_IMG}
                alt="Chocolate caliente con vistas a la montaña nevada"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-12 max-w-xs bg-white border border-brand-line rounded-2xl p-6 shadow-xl">
              <p className="font-cormorant italic text-brand-accent text-xs uppercase tracking-[0.2em]">Especial Familias</p>
              <p className="font-outfit text-brand-primary text-2xl font-semibold mt-2 leading-tight">
                La única decisión difícil del día: chocolate caliente o una última bajada.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 lg:pl-8">
            <p className="overline">Para que no tengas que planificarlo todo</p>
            <h2 className="h1-display mt-4">Lo hemos hecho nosotros por ti.</h2>
            <p className="body-lg mt-6">
              Desde 1969 perfeccionamos el arte de crear viajes de esquí para familias. Más que un producto,
              es nuestra pasión. El resultado es una experiencia donde tu única preocupación es decidir si
              hace falta otra capa de ropa.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {VALUES.map((v) => (
                <div key={v.title} className="flex gap-4">
                  <div className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/8 text-brand-primary">
                    <v.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-outfit font-semibold text-brand-primary">{v.title}</h4>
                    <p className="text-sm text-brand-ink/75 mt-1.5 leading-relaxed">{v.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRIP GRID */}
      <section id="viajes" className="section-y bg-gradient-to-b from-brand-bg to-brand-surface/40">
        <div className="container-prose">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="overline">{TRIPS.length} viajes en familia por temporada</p>
              <h2 className="h1-display mt-4 max-w-3xl">
                Viajes de esquí a la nieve <em className="font-cormorant italic text-brand-accent">en familia</em> — todos los puentes y escapadas de 2026-27.
              </h2>
            </div>
            <p className="body-lg max-w-md md:text-right">
              Cada viaje está pensado para familias con niños: hotel a pie de pista, forfait 3-4 días, clases de esquí opcionales con monitores propios y actividades supervisadas por la tarde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="trips-grid">
            {TRIPS.map((t, i) => (
              <TripCard key={t.slug} trip={t} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* QUICK CONTACT FORM (tras el grid) */}
      <section id="pide-info" className="section-y bg-brand-surface/60">
        <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="overline">Cuéntanos qué buscáis</p>
            <h2 className="h1-display mt-4">
              Pídenos info del viaje que <em className="font-cormorant italic text-brand-accent">te encaje</em>.
            </h2>
            <p className="body-lg mt-6">
              Elige en el desplegable el viaje que más te encaje y Club Amistad contactará contigo con toda
              la información: fechas, precios, alojamiento y cómo reservar plaza.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ContactForm idPrefix="home-viajes" showHeader={false} />
          </div>
        </div>
      </section>

      {/* DIRECTOR / TRUST */}
      <section className="section-y bg-brand-primary text-white relative overflow-hidden">
        <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] max-w-md rounded-[2rem] overflow-hidden">
              <img
                src={DIRECTOR_IMG}
                alt="José María Gonzalo Dueñas, director de Club Amistad"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="font-cormorant italic text-sm uppercase tracking-[0.24em] text-brand-warm">
              La persona que garantiza tu experiencia
            </p>
            <h2 className="font-outfit text-3xl md:text-5xl font-semibold mt-4 leading-tight text-white">
              José María Gonzalo Dueñas
            </h2>
            <p className="text-white/70 mt-1 text-sm md:text-base">Director de Club Amistad</p>
            <blockquote className="mt-8 font-cormorant text-xl md:text-2xl leading-relaxed text-white/90 max-w-2xl border-l-2 border-brand-accent pl-6 italic">
              «Como padre y apasionado del esquí, sé perfectamente que los mejores momentos en la nieve no
              se miden en kilómetros de pistas, sino en las risas de tus hijos. Mi compromiso es encargarme
              de toda la parte compleja para que vosotros solo creéis recuerdos imborrables.»
            </blockquote>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 text-sm text-white/80">
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-brand-warm" /> 56 años de experiencia</div>
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-brand-warm" /> Miles de familias atendidas</div>
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-brand-warm" /> Club de esquí líder en Madrid</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials
        eyebrow="Reseñas reales de Google"
        title="La confianza no se dice, se demuestra."
        intro="Estas opiniones están recopiladas directamente de nuestra ficha de Google Business — reseñas verificadas de familias que ya han viajado con nosotros. Puedes contrastarlas cuando quieras."
      />

      {/* CONTACT CTA */}
      <section id="contacto" className="section-y">
        <div className="container-prose">
          <div className="text-center max-w-3xl mx-auto">
            <p className="overline">¿Listos para empezar?</p>
            <h2 className="h1-display mt-4">
              Hablemos de <em className="font-cormorant italic text-brand-accent">vuestro</em> próximo recuerdo.
            </h2>
            <p className="body-lg mt-6">
              Dinos cuál es el viaje que más te encaja y Club Amistad contactará contigo para resolver tus
              dudas y dejar la reserva lista sin complicaciones.
            </p>
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <ContactForm idPrefix="home-contacto" showHeader={false} />
          </div>

          <div className="mt-16 max-w-4xl mx-auto">
            <p className="overline text-center">O ve directo a un viaje</p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {TRIPS.map((t) => (
                <Link
                  key={t.slug}
                  to={`/viajes-familia-esquiar/${t.slug}`}
                  data-testid={`cta-trip-${t.slug}`}
                  className="card-soft p-5 flex items-center justify-between text-left group"
                >
                  <div>
                    <p className="text-xs uppercase tracking-wider text-brand-accent font-medium">{t.season}</p>
                    <h3 className="font-outfit text-lg text-brand-primary font-semibold mt-1">{t.name}</h3>
                    <p className="text-xs text-brand-muted mt-0.5">{t.dates_short}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-brand-primary group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const PAINS = [
  {
    title: "Un mar de pestañas abiertas",
    body: "Pasáis vuestras noches libres comparando hoteles, forfaits, escuelas y apartamentos hasta que el cerebro parece una hoja de cálculo a punto de colapsar.",
  },
  {
    title: "La presión de ser “Director de Diversión”",
    body: "Te preocupa que el destino no encaje con los niños, que se aburran, o que después de tanto esfuerzo la experiencia no esté a la altura.",
  },
  {
    title: "La ilusión se mezcla con la ansiedad",
    body: "¿Será un entorno seguro? ¿Y si olvido algo? ¿Vamos a poder descansar de verdad? Pequeñas dudas que crecen hasta robarte la emoción previa.",
  },
  {
    title: "Llega el viaje… y sigues trabajando",
    body: "En lugar de unas vacaciones, sientes el peso de la responsabilidad. Eso no son unas vacaciones de verdad. No para ti, y tampoco para tu familia.",
  },
];

const VALUES = [
  {
    icon: Ticket,
    title: "Forfaits en mano, sin colas",
    body: "Llegas y tus forfaits ya te esperan. Tu tiempo en la nieve empieza desde el minuto uno.",
  },
  {
    icon: Headphones,
    title: "Guía 24/7 en destino",
    body: "Un experto local disponible para cualquier duda. La tranquilidad de no estar nunca solos.",
  },
  {
    icon: Sparkles,
    title: "Magia para los niños",
    body: "Actividades gratuitas supervisadas por la tarde. Diversión para ellos, descanso para ti.",
  },
  {
    icon: HeartHandshake,
    title: "Clases que enamoran",
    body: "Día completo desde 4 años con monitores propios. Aprenden seguros mientras tú esquías.",
  },
  {
    icon: Mountain,
    title: "Hoteles a pie de pista",
    body: "Del hotel a la nieve en segundos, no en coche. Sin equipajes, sin parkings, sin esperas.",
  },
  {
    icon: HeartHandshake,
    title: "Asesor que cuida de ti",
    body: "Servicio antes, durante y después del viaje. Cualquier imprevisto, una anécdota más.",
  },
];
